import React, {
    useCallback, memo, useContext, useMemo,
} from 'react';
import { get, includes, isEmpty } from 'lodash';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Toolbar from '../../../widgets/rich-text-editor/textEditorHeader/Toolbar';
import ButtonWithOptions from '../../common/button-with-options/ButtonWithOptions';
import useStyle from './TemplateTextToolbarStyle';
import {
    TemplateTextValueDisplayLabelMap,
    ContactCandidateTemplateButtons,
    TemplateTextLinks,
    getTemplateButtons,
    TextTemplateValues,
} from './template-text-plugin/TemplateTextConsts';
import {
    generateTemplateText,
    getTemplateTextOptions,
    getToolbarOptionsTemplateTextValueLabelMap,
} from './template-text-plugin/TemplateTextUtils';
import UtilsService from '../../../services/UtilsService';
import { insertText, insertLink } from '../../../widgets/rich-text-editor/TextEditorUtils';
import AuthorizationContext from '../../../contexts/AuthorizationContext';
import TrackingContext from '../../../contexts/TrackingContext';
import SettingsContext from '../../../contexts/SettingsContext';
import {
    HOTJAR_TAGS,
    TRACKING_EVENTS,
} from '../../../constants/TrackingConstants';
import PositionContext from '../../../contexts/PositionContext';
import EmployerContext from '../../../contexts/EmployerContext';
import CampaignContext from '../../../contexts/CampaignContext';

// Modified by AI on 05/26/2026. Edit #1.
const TemplateTextToolbar = ({
    className = '',
    editorState = {},
    setEditorState,
    onToggleBlock = () => {},
    onToggleInline = () => {},
    alignment,
    type,
    isHeaderFocused,
    isFeedbackActionShown = true,
    areStylingBtnsShown = true,
    templateSelect = null,
    onChangeInternal,
    textTemplateValues,
    buttonTemplateValues,
    isTemplateTextShown = true,
    campaignSenderType,
}) => {
    const { t } = useTranslation();
    const { trackEvent, trackTag } = useContext(TrackingContext);
    const { position } = useContext(PositionContext);
    const { employer } = useContext(EmployerContext);
    const { isAtsJobUrlAvailable } = useContext(CampaignContext);
    const { systemPreferences: { isQuestionAndCommentEmailOptionsEnabled } } = useContext(SettingsContext);

    // Temporary enable quick buttons for all employers
    const isFeedbackBtnEnabled = get(employer, 'settings.isShowFeedbackBtnOnEmailTemplate', true);

    const isTemplateActionsShown = isFeedbackBtnEnabled && !isHeaderFocused && isFeedbackActionShown;
    const classes = useStyle();
    const rootClasses = UtilsService.getClassName([
        className,
        classes.root,
    ]);

    const { user } = useContext(AuthorizationContext);

    const templateTextOptions = useMemo(() => {
        const useDefaultOptions = isEmpty(textTemplateValues);

        return useDefaultOptions
            ? getTemplateTextOptions(user, {
                isQuestionAndCommentEmailOptionsEnabled,
                isAtsJobUrlAvailable,
                campaignSenderType,
            })
            : Object.values(textTemplateValues).map((value) => ({
                label: getToolbarOptionsTemplateTextValueLabelMap(value),
                value,
            }));
    }, [
        user,
        isQuestionAndCommentEmailOptionsEnabled,
        isAtsJobUrlAvailable,
        textTemplateValues,
        campaignSenderType,
    ]);

    const trackChanges = useCallback(() => {
        if (type === 'emailBody') {
            trackEvent(TRACKING_EVENTS.EMAIL_EDITOR_SELECTED_AUTO_TEXT_BODY, {
                positionId: position._id,
                positionName: position.jobTitle,
                employerId: position.employerId,
            });

            trackTag([HOTJAR_TAGS.EMAIL_EDITOR_SELECTED_AUTO_TEXT_BODY]);
        } else if (type === 'emailSubject') {
            trackEvent(TRACKING_EVENTS.EMAIL_EDITOR_SELECTED_AUTO_TEXT_SUBJECT, {
                positionId: position._id,
                positionName: position.jobTitle,
                employerId: position.employerId,
            });

            trackTag([HOTJAR_TAGS.EMAIL_EDITOR_SELECTED_AUTO_TEXT_SUBJECT]);
        }
    }, [type]);

    const onOptionClick = useCallback((value) => {
        let newEditorState = null;

        const isLink = includes(TemplateTextLinks, value);

        if (isLink) {
            newEditorState = insertLink(editorState, value, TemplateTextValueDisplayLabelMap[value]);
        } else {
            newEditorState = insertText(editorState, generateTemplateText(value));
        }

        trackChanges();

        onChangeInternal(newEditorState);
    }, [editorState, setEditorState, trackChanges]);

    const onTemplateButtonsSelect = useCallback((value) => {
        let newEditorState = null;

        newEditorState = insertText(editorState, generateTemplateText(value));

        trackChanges();

        setEditorState(newEditorState);
    }, [editorState, setEditorState, trackChanges]);

    const templateButtons = useMemo(() => {
        const useDefaultOptions = isEmpty(buttonTemplateValues);
        return useDefaultOptions
            ? ContactCandidateTemplateButtons
            : getTemplateButtons(buttonTemplateValues);
    }, [
        buttonTemplateValues,
        ContactCandidateTemplateButtons,
    ]);

    return (
        <Toolbar
            className={rootClasses}
            alignment={alignment}
            editorState={editorState}
            onToggleBlock={onToggleBlock}
            onToggleInline={onToggleInline}
            areStylingBtnsShown={areStylingBtnsShown}
            templateSelect={templateSelect}
        >
            <div className={classes.autoTextButtonContainer}>
                {
                    isTemplateActionsShown && <ButtonWithOptions
                        className={classes.autoTextButton}
                        label={'Quick Buttons'}
                        options={templateButtons}
                        onOptionClick={onTemplateButtonsSelect}
                        placement={'top-end'}
                    />
                }
                {
                    isTemplateTextShown && <ButtonWithOptions
                        className={classes.buttonWithOptions}
                        label={t('CONTACT_CANDIDATE_EMAIL_FORM_BODY_AUTO_TEXT')}
                        options={templateTextOptions}
                        onOptionClick={onOptionClick}
                        placement={'top-end'}
                    />
                }

            </div>
        </Toolbar>
    );
};

TemplateTextToolbar.propTypes = {
    className: PropTypes.string,
    editorState: PropTypes.object,
    onToggleBlock: PropTypes.func,
    onToggleInline: PropTypes.func,
    areStylingBtnsShown: PropTypes.bool,
    isFeedbackActionShown: PropTypes.bool,
    templateSelect: PropTypes.node,
    isTemplateTextShown: PropTypes.bool,
    campaignSenderType: PropTypes.string,
};

export default memo(TemplateTextToolbar);
